import React, { useEffect, useState, useCallback } from "react";
import { FiRotateCcw, FiSearch, FiPackage, FiRefreshCw, FiX, FiArrowLeft } from "react-icons/fi";
import { useSearchParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "../../Utils/axiosInstance";
import AppLoader from "../../Components/Common/AppLoader";
import Pagination from "../../Components/Common/Pagination";
import { useLockBodyScroll } from "../../Components/Hooks/useLockBodyScroll";
import SupplierReturnListItem from "../../Components/UserSideComponents/SupplierReturns/SupplierReturnListItem";
import SupplierReturnDetailCard from "../../Components/UserSideComponents/SupplierReturns/SupplierReturnDetailCard";
import { assets } from "../../Assets/assets";

/* ─── Skeleton Loading Cards ─────────────────────────────── */
const SkeletonCard = () => (
  <div className="p-3 rounded-xl border border-gray-200 bg-white space-y-2.5 animate-pulse">
    <div className="flex justify-between items-center">
      <div className="h-3.5 w-24 bg-gray-200 rounded" />
      <div className="h-3.5 w-14 bg-gray-200 rounded" />
    </div>
    <div className="flex items-center gap-2.5">
      <div className="w-10 h-10 bg-gray-200 rounded-xl" />
      <div className="space-y-1.5 flex-1">
        <div className="h-3 w-3/4 bg-gray-200 rounded" />
        <div className="h-2.5 w-1/2 bg-gray-200 rounded" />
      </div>
    </div>
    <div className="h-6 bg-gray-100 rounded-lg" />
  </div>
);

/* ─── Empty State ────────────────────────────────────────── */
const EmptyReturns = () => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <img src={assets.noordervector || "/assets/banner.png"} alt="No Returns" className="w-56 md:w-72 mb-4 opacity-90" />
    <h3 className="text-base sm:text-lg font-bold text-gray-700">No Return Orders Found</h3>
    <p className="text-xs text-gray-400 mt-1 max-w-sm leading-relaxed">
      No return requests or dispute claims have been raised for your orders.
    </p>
  </div>
);

const SupplierReturnedOrders = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const highlightId = searchParams.get("highlight") || searchParams.get("returnId");

  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);

  useLockBodyScroll(false);

  const fetchReturns = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      const res = await axios.get(`/return/supplier/returns?page=${page}&limit=${limit}`);
      if (res.data?.success) {
        const list =
          res.data.data?.returns ||
          res.data.data ||
          (Array.isArray(res.data.data) ? res.data.data : []);
        const returnsList = Array.isArray(list) ? list : [];
        setReturns(returnsList);
        setPagination(res.data.pagination || null);

        // Keep selected in sync or auto-select first / highlight item on desktop
        setSelectedReturn((prev) => {
          if (highlightId) {
            const foundHighlight = returnsList.find(
              (r) =>
                r._id === highlightId ||
                r.orderId?._id === highlightId ||
                r.uniqueCaseId?.toLowerCase() === highlightId.toLowerCase()
            );
            if (foundHighlight) return foundHighlight;
          }
          if (prev) {
            const updated = returnsList.find((r) => r._id === prev._id);
            if (updated) return updated;
          }
          if (returnsList.length > 0 && window.innerWidth >= 768) {
            return returnsList[0];
          }
          return null;
        });
      }
    } catch (err) {
      console.error("fetchReturns error:", err);
      setError(true);
      toast.error("Failed to load return orders");
    } finally {
      setLoading(false);
    }
  }, [page, limit, highlightId]);

  useEffect(() => {
    fetchReturns();
  }, [fetchReturns]);

  const filteredReturns = returns.filter((r) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    const caseId = r.uniqueCaseId || "";
    const orderNum = typeof r.orderId === "object" ? r.orderId?.orderNumber || "" : r.orderId || "";
    const quoteNum =
      typeof r.quotationId === "object"
        ? r.quotationId?.quotationUniqueNumber || r.quotationId?.quotationNumber || ""
        : r.quotationId || "";
    const reason = r.reason || "";

    return (
      caseId.toLowerCase().includes(q) ||
      orderNum.toLowerCase().includes(q) ||
      quoteNum.toLowerCase().includes(q) ||
      reason.toLowerCase().includes(q)
    );
  });

  const handleSelectReturn = (item) => {
    setSelectedReturn(item);
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBFB]">
      {/* ── HERO BANNER ──────────────────────────────────────────────────────── */}
      <div
        className="w-full h-[130px] sm:h-[180px] md:h-[220px] flex items-center justify-center bg-no-repeat bg-center bg-cover relative"
        style={{ backgroundImage: `url(${assets.requirementsbanner || "/assets/banner.png"})` }}
      >
        <div className="relative z-10 text-center px-4">
          <h1 className="text-subPrimaryColor text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-center flex items-center justify-center gap-2">
            <FiRotateCcw className="text-2xl sm:text-3xl" /> Supplier Returned Orders
          </h1>
          <p className="text-subPrimaryColor text-xs sm:text-sm font-medium mt-1">
            Manage and track buyer return shipments, disputes, and warehouse deliveries
          </p>
        </div>
      </div>

      {/* ── MAIN CONTAINER ───────────────────────────────────────────────────── */}
      <div className="2xl:max-w-7xl mx-auto px-4 md:px-8 py-5 sm:py-8 mb-16 md:mb-0">
        {/* Loading State */}
        {loading && <AppLoader message="Loading Return Orders..." />}

        {/* Error State */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-red-500 font-medium mb-3">
              Failed to load supplier return orders
            </p>
            <button
              onClick={fetchReturns}
              className="px-4 py-2 bg-primaryColor text-white rounded-xl text-sm font-semibold hover:bg-primaryColor/90 shadow-sm"
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && returns.length === 0 && (
          <EmptyReturns />
        )}

        {/* Returns Layout */}
        {!loading && !error && returns.length > 0 && (
          <>
            {/* ── MOBILE VIEW ──────────────────────────────────────────────── */}
            <div className="md:hidden">
              {!selectedReturn ? (
                <>
                  {/* Search Input */}
                  <div className="mb-4">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search by Case ID, Order #"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor text-sm"
                      />
                      <FiSearch className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <FiX />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Returns List */}
                  <div className="space-y-3 mb-4">
                    {filteredReturns.map((item) => (
                      <SupplierReturnListItem
                        key={item._id}
                        item={item}
                        isActive={false}
                        onClick={() => handleSelectReturn(item)}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {pagination && pagination.totalPages > 1 && (
                    <Pagination
                      pagination={pagination}
                      page={page}
                      setPage={setPage}
                      limit={limit}
                      setLimit={setLimit}
                    />
                  )}
                </>
              ) : (
                <>
                  {/* Back to list button */}
                  <button
                    onClick={() => setSelectedReturn(null)}
                    className="flex items-center gap-2 text-primaryColor mb-3 font-semibold text-sm px-1"
                  >
                    <FiArrowLeft className="w-4 h-4" /> Back to Returned Orders
                  </button>

                  {/* Detail Panel */}
                  <div className="bg-transparent sm:bg-white rounded-2xl p-0 sm:p-4 sm:border sm:border-gray-200">
                    <SupplierReturnDetailCard
                      returnId={selectedReturn._id}
                      item={selectedReturn}
                      onBack={() => setSelectedReturn(null)}
                      onRefresh={fetchReturns}
                    />
                  </div>
                </>
              )}
            </div>

            {/* ── DESKTOP VIEW (Exact matching Supplier Orders grid) ───────────── */}
            <div className="hidden md:block">
              <div className="grid md:grid-cols-12 gap-5">
                {/* Left Sidebar - Returns List (col-span-4 lg:col-span-3) */}
                <div className="md:col-span-4 lg:col-span-3 space-y-4 sticky top-16 h-[calc(100vh-4rem)] overflow-hidden">
                  <div className="h-full overflow-y-scroll space-y-3 pb-8 pr-1">
                    {/* Search */}
                    <div className="relative mb-3">
                      <input
                        type="text"
                        placeholder="Search by Case ID, Order #"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor text-xs"
                      />
                      <FiSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <FiX className="text-xs" />
                        </button>
                      )}
                    </div>

                    {/* Returns List */}
                    <div className="space-y-2.5">
                      {filteredReturns.map((item) => (
                        <SupplierReturnListItem
                          key={item._id}
                          item={item}
                          isActive={selectedReturn?._id === item._id}
                          onClick={() => handleSelectReturn(item)}
                        />
                      ))}
                    </div>

                    {/* Pagination */}
                    {pagination && pagination.totalPages > 1 && (
                      <div className="pt-2">
                        <Pagination
                          pagination={pagination}
                          page={page}
                          setPage={setPage}
                          limit={limit}
                          setLimit={setLimit}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Content - Return Details (col-span-8 lg:col-span-9) */}
                <div className="md:col-span-8 lg:col-span-9">
                  {selectedReturn ? (
                    <SupplierReturnDetailCard
                      returnId={selectedReturn._id}
                      item={selectedReturn}
                      onRefresh={fetchReturns}
                    />
                  ) : (
                    <div className="bg-white p-12 rounded-2xl border border-dashed border-gray-300 text-center text-gray-400 space-y-2">
                      <FiRotateCcw className="text-4xl text-gray-300 mx-auto" />
                      <p className="font-bold text-sm text-gray-500">Select a Return Order</p>
                      <p className="text-xs text-gray-400">
                        Click on any return dispute from the list on the left to view detailed case information and delivery actions.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SupplierReturnedOrders;
